import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AlertTriangle, ChevronDown, ChevronRight, Info, RefreshCcw, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  getFiscalProfile,
  rederiveFiscalProfile,
  type FiscalProfileResponse,
} from "@/services/fiscalProfileService";

interface Props {
  tenantId: string;
}

export function TenantFiscalProfileCard({ tenantId }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [showTrace, setShowTrace] = useState(false);
  const [showObligaciones, setShowObligaciones] = useState(false);

  const query = useQuery({
    queryKey: ["fiscal-profile", tenantId],
    queryFn: () => getFiscalProfile(tenantId),
  });

  const rederive = useMutation({
    mutationFn: () => rederiveFiscalProfile(tenantId),
    onSuccess: (res) => {
      toast.success(
        `Deriver re-run · matched ${res.matched.length} obligación(es)` +
          (res.needsReview ? ` · ${res.reviewReasons.length} review reason(s)` : ""),
      );
      queryClient.invalidateQueries({ queryKey: ["fiscal-profile", tenantId] });
    },
    onError: () => toast.error("Could not re-run deriver"),
  });

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fiscal profile</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">Cargando…</CardContent>
      </Card>
    );
  }

  const data = query.data;
  if (!data || !data.profile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fiscal profile</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Sin perfil fiscal registrado. Se generará automáticamente cuando el
          paso "Consulta de Datos Registrales" del onboarding se complete.
        </CardContent>
      </Card>
    );
  }

  const { profile, registration, derived } = data;
  const trace = derived?.trace;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="space-y-1">
          <CardTitle className="text-base">Fiscal profile</CardTitle>
          {derived?.derivedAt ? (
            <p className="text-xs text-muted-foreground">
              Derivado {new Date(derived.derivedAt).toLocaleString()}
            </p>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => rederive.mutate()}
          disabled={rederive.isPending}
        >
          <RefreshCcw className="h-4 w-4" />
          <span className="ml-2">Re-derivar</span>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {derived?.needsReview ? (
          <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-amber-900">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4" />
              <span>Verificá los datos derivados</span>
            </div>
            <ul className="mt-2 list-disc pl-5 space-y-1 text-xs">
              {derived.reviewReasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <TooltipProvider delayDuration={200}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Tipo de empresa"
              value={profile.companyType ?? "—"}
              source={trace?.companyTypeSource}
            />
            <Field
              label="Tipo de contribuyente"
              value={profile.taxpayerType ?? "—"}
              source={trace?.taxpayerTypeSource}
            />
            <Field
              label="IVA regime"
              value={profile.ivaRegime ?? "—"}
              source={trace?.ivaRegimeSource}
            />
            <Field label="IVA frecuencia" value={profile.ivaFrequency ?? "—"} />
            <Field label="IRAE frecuencia" value={profile.iraeFrequency ?? "—"} />
            <Field
              label="Fin ejercicio"
              value={profile.fiscalYearEnd}
              source={trace?.fiscalYearEndSource}
            />
            <Field label="Inicio actividades" value={profile.activityStartDate || "—"} />
            <Field
              label="Exportador"
              value={profile.isExporter ? "Sí" : "No"}
              source={trace?.isExporterSource}
            />
            <Field label="Tiene empleados" value={profile.hasEmployees ? "Sí" : "No"} />
            <Field
              label="N° empresa BPS"
              value={profile.bpsCompanyNumber ?? "—"}
            />
          </div>
        </TooltipProvider>

        <div>
          <p className="text-xs uppercase text-muted-foreground mb-1">Tributos</p>
          <div className="flex flex-wrap gap-1">
            {profile.taxes.length > 0 ? (
              profile.taxes.map((t) => (
                <Badge key={t} variant="secondary">
                  {t}
                </Badge>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        </div>

        <Separator />

        {registration ? (
          <div>
            <button
              type="button"
              onClick={() => setShowObligaciones((s) => !s)}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              {showObligaciones ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              Datos registrales DGI ({registration.obligaciones.length} obligación(es),{" "}
              {registration.actividades.length} actividad(es))
            </button>
            {showObligaciones ? (
              <div className="mt-2 space-y-3">
                <div>
                  <p className="text-xs uppercase text-muted-foreground mb-1">
                    Tipo de entidad
                  </p>
                  <p>{registration.tipoEntidad ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground mb-1">Grupo</p>
                  <p>{registration.grupo ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground mb-1">
                    Obligaciones
                  </p>
                  <ul className="text-xs space-y-1">
                    {registration.obligaciones.map((o, i) => (
                      <li key={i} className="font-mono">
                        {o.nombre}
                        {o.caracteristica ? ` · ${o.caracteristica}` : ""}
                        {o.fechaInicio ? ` · desde ${o.fechaInicio}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground mb-1">
                    Actividades CIIU
                  </p>
                  <ul className="text-xs space-y-1">
                    {registration.actividades.map((a, i) => (
                      <li key={i}>
                        <span className="font-mono">{a.codigo}</span> · {a.descripcion}
                        {a.esPrincipal ? (
                          <Badge variant="outline" className="ml-2">
                            principal
                          </Badge>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {trace?.obligacionesMatched && trace.obligacionesMatched.length > 0 ? (
          <div>
            <button
              type="button"
              onClick={() => setShowTrace((s) => !s)}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              {showTrace ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              <Sparkles className="h-3.5 w-3.5" />
              Audit trace ({trace.obligacionesMatched.length} regla(s) aplicada(s))
            </button>
            {showTrace ? (
              <ul className="mt-2 text-xs space-y-1">
                {trace.obligacionesMatched.map((m, i) => (
                  <li key={i}>
                    <span className="font-mono">{m.nombre}</span>{" "}
                    <span className="text-muted-foreground">→ {m.effects.join(", ")}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  source,
}: {
  label: string;
  value: string;
  source?: string;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-1 text-xs uppercase text-muted-foreground">
        <span>{label}</span>
        {source ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 cursor-help opacity-60" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <span className="text-xs">{source}</span>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <p>{value}</p>
    </div>
  );
}

export type { FiscalProfileResponse };
